import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Arac, IsEmriOzet, Musteri } from '@nero/shared';
import {
  CARI_ISLEM_ACIKLAMA, CARI_ISLEM_ADI, CARI_ISLEM_TURLERI, CARI_KAYNAK_ADI,
  ARACSIZ, bugun, para, plakaGoster, tarihGoster,
} from '@nero/shared';
import type { CariIslemTuru } from '@nero/shared';
import { ApiHatasi, api, useEylem, useVeri } from '../lib/veri.js';
import { useOturum } from '../lib/oturum.js';
import { useBildirim } from '../lib/bildirim.js';
import { HataKutusu, Kart, KartBaslik, Olcum, Rozet, UyariSerit, Yukleniyor } from '../bilesenler/temel.js';
import { LogAnalizKarti } from './ortak/LogAnalizKarti.js';
import { Girdi, MetinAlani, ParaGirdi, Secim } from '../bilesenler/form.js';
import { OnayPenceresi, Pencere } from '../bilesenler/Pencere.js';
import { Simge } from '../bilesenler/Simge.js';
import { DurumRozeti, OdemeRozeti } from './ortak/rozetler.js';
import { MusteriPenceresi } from './Musteriler.js';

interface CariOzet {
  borc: number; alacak: number; bakiye: number;
  isEmriBorc: number; borcEkleme: number; tahsilat: number; alacakEkleme: number;
  islem: Record<CariIslemTuru, number>;
}

interface Detay {
  musteri: Musteri;
  araclar: Arac[];
  cari: CariOzet;
  isEmirleri: IsEmriOzet[];
}

interface CariSatir {
  tarih: string; kaynak: string; aciklama: string; borc: number; alacak: number; ref_id: number;
  islem_turu: CariIslemTuru | null;
  kayit_kaynagi: string | null;
  duzenlenebilir: boolean;
}

interface ManuelKayit {
  id: number; tarih: string; islem_turu: CariIslemTuru; tutar: number; not: string;
}

export function MusteriDetayi() {
  const { id } = useParams();
  const git = useNavigate();
  const bildir = useBildirim();
  const { yetkili } = useOturum();
  const { veri, yukleniyor, hata, yenile } = useVeri<Detay>(id ? `/musteriler/${id}` : null);
  /* CARİ DÖKÜMÜ İSTEĞİ YALNIZCA YÖNETİCİ İÇİN ATILIR.
     Koşul `musteri.goruntule` idi; sunucu ise bu uç için
     `kasa.goruntule` istiyor. Sonuç: personel her müşteri detayında
     kullanamayacağı bir uca istek atıyor, 403 alıyor ve ekranda
     görünmeyen bir hata kutusu doluyordu. Doğru koşul, sunucunun
     istediği izindir. */
  const cari = useVeri<{ ozet: Detay['cari']; hareketler: CariSatir[] }>(
    yetkili('kasa.goruntule') && id ? `/musteriler/${id}/cari` : null,
  );
  const [duzenle, setDuzenle] = useState(false);
  const [aracAcik, setAracAcik] = useState<Arac | 'yeni' | null>(null);
  const [silAcik, setSilAcik] = useState(false);
  const [duzenlenenCari, setDuzenlenenCari] = useState<ManuelKayit | null>(null);

  const sil = useEylem(async () => {
    await api.sil(`/musteriler/${id}`);
    bildir('Müşteri çöpe taşındı');
    git('/musteriler');
  });

  if (hata) return <div className="sayfa"><HataKutusu hata={hata} yenile={yenile} /></div>;
  if (yukleniyor || !veri) return <div className="sayfa"><Yukleniyor satir={5} /></div>;

  const m = veri.musteri;
  const finansGor = yetkili('kasa.goruntule');

  return (
    <div className="sayfa">
      <div className="sayfa-baslik">
        <div>
          <Link to="/musteriler" className="btn btn-sade btn-kucuk" style={{ marginBottom: 6, marginLeft: -8 }}>
            <Simge ad="ok-sol" boyut={14} /> Müşteriler
          </Link>
          <h1>{m.ad}</h1>
          <div className="alt">
            {[m.telefon, m.eposta].filter(Boolean).join(' · ') || 'İletişim bilgisi girilmemiş'}
          </div>
        </div>
        <div className="eylemler">
          {m.telefon && (
            <a className="btn btn-ikincil" href={`tel:${m.telefon.replace(/\s/g, '')}`}>
              <Simge ad="telefon" className="simge" /> Ara
            </a>
          )}
          {yetkili('musteri.duzenle') && (
            <button className="btn btn-ikincil" onClick={() => setDuzenle(true)}>
              <Simge ad="kalem" className="simge" /> Düzenle
            </button>
          )}
        </div>
      </div>

      {/* Personel yalnızca GÜNCEL BAKİYEYİ görür: müşteri "ne kadar borcum
          var?" diye sorduğunda cevap verebilsin. Borcun dökümü, tahsilat
          geçmişi ve toplam harcama yöneticiye özeldir. Sunucu da yanıtı
          aynı kurala göre kısar. */}
      {veri.cari && (
        <div className={finansGor ? 'izgara izgara-3' : 'izgara'} style={{ marginBottom: 'var(--b6)' }}>
          {finansGor && <Olcum etiket="Toplam borç" deger={para(veri.cari.borc ?? 0)} />}
          {finansGor && <Olcum etiket="Tahsil edilen" deger={para(veri.cari.alacak ?? 0)} vurgu="olumlu" />}
          <Olcum
            etiket="Bakiye" deger={para(veri.cari.bakiye)} buyuk
            vurgu={veri.cari.bakiye > 0.009 ? 'uyari' : 'olumlu'}
            alt={veri.cari.bakiye > 0.009 ? 'Müşteri borçlu' : 'Bakiye kapalı'}
          />
        </div>
      )}

      <div className="izgara izgara-yan">
        <div className="sutun" style={{ gap: 'var(--b4)' }}>
          <Kart>
            <KartBaslik baslik="İş emirleri" />
            {veri.isEmirleri.length === 0 ? (
              <div className="kart-govde" style={{ color: 'var(--metin-3)', fontSize: 'var(--p-13)' }}>
                Bu müşteri için henüz iş emri açılmadı.
              </div>
            ) : (
              <div className="tablo-sarma">
                <table className="tablo">
                  <thead><tr><th>No</th><th>Araç</th><th>İşlem</th><th>Tarih</th><th>Durum</th><th className="sag">Tutar</th><th>Ödeme</th></tr></thead>
                  <tbody>
                    {veri.isEmirleri.map((i) => (
                      <tr key={i.id} className="tiklanabilir" onClick={() => git(`/is-emirleri/${i.id}`)}>
                        <td className="ikincil sayi dar">{i.no}</td>
                        <td className="ana">{plakaGoster(i.plaka) || ARACSIZ}</td>
                        <td className="ikincil">{i.islem || '—'}</td>
                        <td className="ikincil dar">{tarihGoster(i.acilis_at)}</td>
                        <td className="dar"><DurumRozeti durum={i.durum} /></td>
                        <td className="sag para">{para(i.tutar)}</td>
                        <td className="dar"><OdemeRozeti durum={i.odeme_durumu} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Kart>

          {finansGor && cari.hata && (
            <Kart>
              <KartBaslik baslik="Cari hareketler" />
              <div className="kart-govde">
                <HataKutusu hata={cari.hata} yenile={cari.yenile} />
              </div>
            </Kart>
          )}
          {finansGor && !cari.hata && cari.yukleniyor && (
            <Kart>
              <KartBaslik baslik="Cari hareketler" />
              <div className="kart-govde"><Yukleniyor /></div>
            </Kart>
          )}
          {finansGor && cari.veri && (
            <Kart>
              <KartBaslik baslik="Cari hareketler">
                {yetkili('kasa.islem') && (
                  <CariPencere musteriId={Number(id)} yenile={() => { yenile(); cari.yenile(); }} />
                )}
              </KartBaslik>
              <div className="kart-govde sutun" style={{ gap: 'var(--b3)' }}>
                <div className="izgara izgara-4" style={{ gap: 'var(--b3)', fontSize: 'var(--p-12)' }}>
                  {CARI_ISLEM_TURLERI.map((t) => (
                    <div key={t}>
                      <div style={{ color: 'var(--metin-3)' }}>{CARI_ISLEM_ADI[t]}</div>
                      <strong className="para">{para(cari.veri!.ozet.islem[t])}</strong>
                    </div>
                  ))}
                </div>
                <div className="izgara izgara-2" style={{ gap: 'var(--b3)', fontSize: 'var(--p-12)' }}>
                  <div>
                    <div style={{ color: 'var(--metin-3)' }}>İş emri borcu</div>
                    <strong className="para">{para(cari.veri.ozet.isEmriBorc)}</strong>
                  </div>
                  <div>
                    <div style={{ color: 'var(--metin-3)' }}>Kasa tahsilatı</div>
                    <strong className="para">{para(cari.veri.ozet.tahsilat)}</strong>
                  </div>
                </div>
              </div>
              {cari.veri.hareketler.length === 0 ? (
                <div className="kart-govde" style={{ color: 'var(--metin-3)', fontSize: 'var(--p-13)' }}>
                  Henüz cari hareket yok.
                </div>
              ) : (
                <div className="tablo-sarma">
                  <table className="tablo">
                    <thead>
                      <tr><th>Tarih</th><th>Açıklama</th><th className="sag">Borç</th><th className="sag">Alacak</th><th /></tr>
                    </thead>
                    <tbody>
                      {cari.veri.hareketler.map((h, i) => (
                        <tr key={`${h.kaynak}-${h.ref_id}-${i}`}>
                          <td className="ikincil dar">{tarihGoster(h.tarih)}</td>
                          <td>
                            <div className="satir" style={{ gap: 6, flexWrap: 'wrap' }}>
                              {h.islem_turu && <Rozet ton="notr" sade>{CARI_ISLEM_ADI[h.islem_turu]}</Rozet>}
                              <span>{h.aciklama}</span>
                            </div>
                            <div className="ikincil">
                              {h.kaynak === 'is_emri' ? 'İş emrinden'
                                : h.kaynak === 'tahsilat' ? 'Kasadan'
                                : CARI_KAYNAK_ADI[h.kayit_kaynagi ?? 'manuel']}
                            </div>
                          </td>
                          <td className="sag para">{h.borc ? para(h.borc) : ''}</td>
                          <td className="sag para" style={{ color: h.alacak ? 'var(--olumlu)' : undefined }}>
                            {h.alacak ? para(h.alacak) : ''}
                          </td>
                          <td className="dar">
                            {h.duzenlenebilir && h.islem_turu && yetkili('kasa.islem') && (
                              <button
                                className="btn btn-sade btn-simge btn-kucuk" aria-label="Düzenle"
                                onClick={() => setDuzenlenenCari({
                                  id: h.ref_id, tarih: h.tarih,
                                  islem_turu: h.islem_turu!,
                                  tutar: h.borc > 0 ? h.borc : h.alacak,
                                  not: h.aciklama === 'Cari kaydı' ? '' : h.aciklama,
                                })}
                              >
                                <Simge ad="kalem" boyut={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Kart>
          )}
        </div>

        <div className="sutun" style={{ gap: 'var(--b4)' }}>
          <Kart>
            <KartBaslik baslik="Araçlar">
              {yetkili('musteri.duzenle') && (
                <button className="btn btn-ikincil btn-kucuk" onClick={() => setAracAcik('yeni')}>
                  <Simge ad="arti" boyut={14} /> Ekle
                </button>
              )}
            </KartBaslik>
            {veri.araclar.length === 0 ? (
              <div className="kart-govde" style={{ color: 'var(--metin-3)', fontSize: 'var(--p-13)' }}>
                Kayıtlı araç yok.
              </div>
            ) : (
              <div className="kart-govde sutun">
                {veri.araclar.map((a) => (
                  /* Yetkisiz kullanıcıya TIKLANABİLİR ama hiçbir şey yapmayan
                     düğme gösterilmesin: düğme yerine düz kutu çizilir. */
                  <button
                    key={a.id}
                    disabled={!yetkili('musteri.duzenle')}
                    onClick={() => setAracAcik(a)}
                    style={{
                      textAlign: 'left', padding: 'var(--b3)', borderRadius: 'var(--kose-s)',
                      border: '1px solid var(--cizgi)', background: 'var(--yuzey-2)',
                      cursor: yetkili('musteri.duzenle') ? 'pointer' : 'default',
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{plakaGoster(a.plaka) || '(plakasız)'}</div>
                    <div style={{ fontSize: 'var(--p-12)', color: 'var(--metin-2)' }}>
                      {[a.marka, a.model, a.motor_kodu].filter(Boolean).join(' · ') || 'Bilgi girilmemiş'}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Kart>

          {/* Her aracın kendi log analiz geçmişi. Kayıtlar veritabanında
              duruyordu ama hiçbir ekranda görünmüyordu. */}
          {veri.araclar.map((a) => (
            <LogAnalizKarti
              key={a.id}
              aracId={a.id}
              baslik={`Log analizleri · ${plakaGoster(a.plaka) || 'plakasız araç'}`}
            />
          ))}

          {m.not && (
            <Kart>
              <KartBaslik baslik="Not" />
              <div className="kart-govde" style={{ fontSize: 'var(--p-13)', whiteSpace: 'pre-wrap' }}>{m.not}</div>
            </Kart>
          )}

          {yetkili('musteri.sil') && (
            <button className="btn btn-tehlike btn-tam" onClick={() => setSilAcik(true)}>
              <Simge ad="cop" className="simge" /> Müşteriyi sil
            </button>
          )}
        </div>
      </div>

      {duzenle && (
        <MusteriPenceresi mevcut={m} kapat={() => setDuzenle(false)} kaydedildi={() => { setDuzenle(false); yenile(); }} />
      )}
      {aracAcik && (
        <AracPenceresi
          musteriId={m.id}
          mevcut={aracAcik === 'yeni' ? undefined : aracAcik}
          kapat={() => setAracAcik(null)}
          kaydedildi={() => { setAracAcik(null); yenile(); }}
        />
      )}
      {duzenlenenCari && (
        <CariPencere
          musteriId={Number(id)}
          mevcut={duzenlenenCari}
          disaridanKapat={() => setDuzenlenenCari(null)}
          yenile={() => { yenile(); cari.yenile(); }}
        />
      )}
      {silAcik && (
        <OnayPenceresi
          baslik="Müşteriyi sil"
          aciklama={`${m.ad} çöpe taşınacak. İş emirleri ve kasa kayıtları silinmez; yönetici çöp kutusundan geri alabilir.`}
          kapat={() => setSilAcik(false)}
          onayla={() => void sil.yurut(undefined as never)}
          calisiyor={sil.calisiyor}
          hata={sil.hata}
        />
      )}
    </div>
  );
}

function AracPenceresi({ musteriId, mevcut, kapat, kaydedildi }: {
  musteriId: number; mevcut?: Arac; kapat: () => void; kaydedildi: () => void;
}) {
  const bildir = useBildirim();
  const [plaka, setPlaka] = useState(mevcut?.plaka ?? '');
  const [marka, setMarka] = useState(mevcut?.marka ?? '');
  const [model, setModel] = useState(mevcut?.model ?? '');
  const [yil, setYil] = useState(mevcut?.yil ? String(mevcut.yil) : '');
  const [motor, setMotor] = useState(mevcut?.motor ?? '');
  const [motorKodu, setMotorKodu] = useState(mevcut?.motor_kodu ?? '');
  const [sanziman, setSanziman] = useState(mevcut?.sanziman ?? '');
  const [vin, setVin] = useState(mevcut?.vin ?? '');
  const [not, setNot] = useState(mevcut?.not ?? '');
  /* Yeni müşteri formuyla AYNI kural: aynı plaka ikinci kez, hele başka
     bir müşteriye yazılamaz. Sunucu 409 · `PLAKA_VAR:<musteriId>` döner. */
  const [cakisanMusteri, setCakisanMusteri] = useState<number | null>(null);
  const git = useNavigate();

  const { yurut, calisiyor, hata } = useEylem(async () => {
    setCakisanMusteri(null);
    const govde = {
      musteri_id: musteriId, plaka, marka, model,
      yil: yil ? Number(yil) : null, motor, motor_kodu: motorKodu, sanziman, vin, not,
    };
    try {
      if (mevcut) await api.guncelle(`/araclar/${mevcut.id}`, govde);
      else await api.gonder('/araclar', govde);
    } catch (e) {
      const kod = e instanceof ApiHatasi ? (e.kod ?? '') : '';
      if (kod.startsWith('PLAKA_VAR:')) setCakisanMusteri(Number(kod.split(':')[1]));
      throw e;
    }
    bildir(mevcut ? 'Araç güncellendi' : 'Araç eklendi');
    kaydedildi();
  });

  return (
    <Pencere
      baslik={mevcut ? 'Aracı düzenle' : 'Yeni araç'}
      kapat={kapat}
      altBolum={
        <>
          <button className="btn btn-sade" onClick={kapat} disabled={calisiyor}>Vazgeç</button>
          <button className="btn btn-birincil" onClick={() => void yurut(undefined as never)} disabled={calisiyor}>
            {calisiyor ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </>
      }
    >
      <div className="sutun">
        <Girdi etiket="Plaka" value={plaka} onChange={setPlaka} autoFocus autoCapitalize="characters" duzen="plaka" />
        <div className="izgara izgara-2">
          <Girdi etiket="Marka" value={marka} onChange={setMarka} duzen="markaModel" />
          <Girdi etiket="Model" value={model} onChange={setModel} duzen="markaModel" />
          <Girdi etiket="Yıl" value={yil} onChange={setYil} type="number" min={1950} max={2100} />
          <Girdi etiket="Motor" value={motor} onChange={setMotor} placeholder="2.0 Turbo" duzen="markaModel" />
          <Girdi etiket="Motor kodu" value={motorKodu} onChange={setMotorKodu} placeholder="B48B16" duzen="bosluk" />
          <Girdi etiket="Şanzıman" value={sanziman} onChange={setSanziman} placeholder="ZF8HP" duzen="bosluk" />
        </div>
        <Girdi etiket="Şasi no (VIN)" value={vin} onChange={setVin} maxLength={17} />
        <MetinAlani etiket="Not" value={not} onChange={setNot} rows={2} duzen="cumle" />
        {hata && (
          <UyariSerit ton="kritik" baslik={cakisanMusteri ? 'Bu plaka zaten kayıtlı' : undefined}>
            {hata}
            {cakisanMusteri && cakisanMusteri !== musteriId && (
              <div className="satir" style={{ marginTop: 'var(--b2)' }}>
                <button className="btn btn-kucuk btn-birincil"
                  onClick={() => { kapat(); git(`/musteriler/${cakisanMusteri}`); }}>
                  Müşteriyi aç
                </button>
              </div>
            )}
          </UyariSerit>
        )}
      </div>
    </Pencere>
  );
}

/**
 * Manuel cari düzeltmesi: ekleme ve düzenleme aynı pencereden yapılır.
 * `mevcut` verilirse düzenleme kipinde açılır ve silme düğmesi görünür.
 */
function CariPencere({ musteriId, mevcut, disaridanKapat, yenile }: {
  musteriId: number;
  mevcut?: ManuelKayit;
  disaridanKapat?: () => void;
  yenile: () => void;
}) {
  const bildir = useBildirim();
  const { yetkili } = useOturum();
  const [acik, setAcik] = useState(!!mevcut);
  const [tarih, setTarih] = useState(mevcut?.tarih ?? bugun());
  const [islemTuru, setIslemTuru] = useState<CariIslemTuru>(mevcut?.islem_turu ?? 'tahsilat');
  const [tutar, setTutar] = useState(mevcut ? String(mevcut.tutar) : '');
  const [not, setNot] = useState(mevcut?.not ?? '');
  const [silAcik, setSilAcik] = useState(false);

  const kapat = () => { setAcik(false); disaridanKapat?.(); };

  const kaydet = useEylem(async () => {
    const govde = { tarih, islem_turu: islemTuru, tutar: Number(tutar), not };
    if (mevcut) await api.guncelle(`/cari/${mevcut.id}`, govde);
    else await api.gonder(`/musteriler/${musteriId}/cari`, govde);
    bildir(mevcut ? 'Cari kaydı güncellendi' : 'Cari kaydı eklendi');
    yenile();
    kapat();
    setTutar(''); setNot('');
  });

  const sil = useEylem(async () => {
    if (!mevcut) return;
    await api.sil(`/cari/${mevcut.id}`);
    bildir('Cari kaydı çöpe taşındı');
    yenile();
    setSilAcik(false);
    kapat();
  });

  return (
    <>
      {!mevcut && CARI_ISLEM_TURLERI.map((t) => (
        <button
          key={t}
          className="btn btn-ikincil btn-kucuk"
          title={CARI_ISLEM_ACIKLAMA[t]}
          onClick={() => { setIslemTuru(t); setAcik(true); }}
        >
          <Simge ad="arti" boyut={14} /> {CARI_ISLEM_ADI[t]}
        </button>
      ))}
      {acik && (
        <Pencere
          baslik={mevcut ? 'Cari işlemi düzenle' : CARI_ISLEM_ADI[islemTuru]}
          alt={CARI_ISLEM_ACIKLAMA[islemTuru]}
          kapat={kapat}
          altBolum={
            <>
              {mevcut && yetkili('kasa.sil') && (
                <button className="btn btn-tehlike" style={{ marginRight: 'auto' }}
                  onClick={() => setSilAcik(true)} disabled={kaydet.calisiyor}>
                  Sil
                </button>
              )}
              <button className="btn btn-sade" onClick={kapat} disabled={kaydet.calisiyor}>Vazgeç</button>
              <button className="btn btn-birincil" onClick={() => void kaydet.yurut(undefined as never)}
                disabled={kaydet.calisiyor || !(Number(tutar) > 0)}>
                {kaydet.calisiyor ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </>
          }
        >
          <div className="sutun">
            <Girdi etiket="Tarih" type="date" value={tarih} onChange={setTarih} />
            <Secim
              etiket="İşlem" value={islemTuru} onChange={(v) => setIslemTuru(v as CariIslemTuru)}
              ipucu={CARI_ISLEM_ACIKLAMA[islemTuru]}
              secenekler={CARI_ISLEM_TURLERI.map((t) => ({ deger: t, ad: CARI_ISLEM_ADI[t] }))}
            />
            <ParaGirdi etiket="Tutar" deger={tutar} onChange={setTutar} autoFocus />
            <Girdi etiket="Açıklama" value={not} onChange={setNot} placeholder="Neden eklendiği" duzen="cumle" />
            <UyariSerit ton="notr">
              Bu kayıt kasaya girmez; yalnızca cari bakiyeyi etkiler. İşlem türü (Alacak Ekle, Borç Ekle,
              Tahsilat, Ödeme) olduğu gibi saklanır, bakiye yönü ondan hesaplanır. İş emrinden ve kasadan
              gelen satırlar kendi ekranlarından düzeltilir.
            </UyariSerit>
            {kaydet.hata && <UyariSerit ton="kritik">{kaydet.hata}</UyariSerit>}
          </div>
        </Pencere>
      )}
      {silAcik && mevcut && (
        <OnayPenceresi
          baslik="Cari kaydını sil"
          aciklama="Kayıt çöp kutusuna taşınacak; yönetici geri alabilir."
          kapat={() => setSilAcik(false)}
          onayla={() => void sil.yurut(undefined as never)}
          calisiyor={sil.calisiyor}
          hata={sil.hata}
        />
      )}
    </>
  );
}
